import { LexicalEditor } from "lexical";
import { CSSProperties, PointerEvent as ReactPointerEvent, useCallback, useRef } from "react";

const MIN_SIZE = 100;
const CONTAINER_PADDING = 20;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Which edge(s) a handle drags, plus the cursor to show while dragging. */
export interface ResizeDirection {
  cursor: CSSProperties["cursor"];
  x?: "left" | "right";
  y?: "bottom" | "top";
}

interface UseImageResizeParams {
  editor: LexicalEditor;
  imageRef: { current: HTMLElement | null };
  onResizeStart: () => void;
  onResizeEnd: (width: number, height: number) => void;
}

interface UseImageResizeResult {
  startResize: (event: ReactPointerEvent<HTMLDivElement>, direction: ResizeDirection) => void;
}

/**
 * Encapsulates the pointer-driven resize of an image element: it locks the page
 * cursor/selection during the drag, mutates the element's inline size live for an
 * instant preview, and reports the committed dimensions via `onResizeEnd`. The
 * component using it only renders handles and forwards their pointer-down to
 * `startResize`.
 */
export function useImageResize({
  editor,
  imageRef,
  onResizeStart,
  onResizeEnd,
}: UseImageResizeParams): UseImageResizeResult {
  const previousUserSelect = useRef({ priority: "", value: "" });

  const lockPageInteraction = useCallback((cursor: CSSProperties["cursor"]) => {
    const { style } = document.body;
    previousUserSelect.current = {
      priority: style.getPropertyPriority("-webkit-user-select"),
      value: style.getPropertyValue("-webkit-user-select"),
    };
    style.setProperty("cursor", `${cursor}`, "important");
    style.setProperty("-webkit-user-select", "none", "important");
  }, []);

  const unlockPageInteraction = useCallback(() => {
    const { style } = document.body;
    const { priority, value } = previousUserSelect.current;
    style.setProperty("cursor", "default");
    style.setProperty("-webkit-user-select", value, priority);
  }, []);

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, direction: ResizeDirection) => {
      const image = imageRef.current;
      if (!editor.isEditable() || !image) {
        return;
      }
      event.preventDefault();

      const rootRect = editor.getRootElement()?.getBoundingClientRect();
      const maxWidth = rootRect ? rootRect.width - CONTAINER_PADDING : MIN_SIZE;
      const maxHeight = rootRect ? rootRect.height - CONTAINER_PADDING : MIN_SIZE;

      const { width: startWidth, height: startHeight } = image.getBoundingClientRect();
      const ratio = startWidth / startHeight;
      const { clientX: startX, clientY: startY } = event;
      let width = startWidth;
      let height = startHeight;

      lockPageInteraction(direction.cursor);
      onResizeStart();
      // Lock the current displayed size so dragging works from a known baseline.
      image.style.width = `${startWidth}px`;
      image.style.height = `${startHeight}px`;

      const handleMove = (moveEvent: PointerEvent): void => {
        if (direction.x) {
          const dx = direction.x === "right" ? moveEvent.clientX - startX : startX - moveEvent.clientX;
          width = clamp(startWidth + dx, MIN_SIZE, maxWidth);
          image.style.width = `${width}px`;
          // Corner handles keep the aspect ratio by deriving height from width.
          if (direction.y) {
            height = width / ratio;
            image.style.height = `${height}px`;
          }
        } else if (direction.y) {
          const dy = direction.y === "bottom" ? moveEvent.clientY - startY : startY - moveEvent.clientY;
          height = clamp(startHeight + dy, MIN_SIZE, maxHeight);
          image.style.height = `${height}px`;
        }
      };

      const handleUp = (): void => {
        unlockPageInteraction();
        onResizeEnd(Math.round(width), Math.round(height));
        document.removeEventListener("pointermove", handleMove);
        document.removeEventListener("pointerup", handleUp);
      };

      document.addEventListener("pointermove", handleMove);
      document.addEventListener("pointerup", handleUp);
    },
    [editor, imageRef, lockPageInteraction, onResizeEnd, onResizeStart, unlockPageInteraction]
  );

  return { startResize };
}
